import { createTheme } from '@mui/material';

// Figma-inspired dark theme with monochromatic grayscale palette
const figmaDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFFFFF', // Pure white
      light: '#FFFFFF',
      dark: '#E0E0E0',
      contrastText: '#1E1E1E',
    },
    secondary: {
      main: '#ACACAC', // Mid gray
      light: '#D1D1D1',
      dark: '#858585',
      contrastText: '#1E1E1E',
    },
    error: {
      main: '#EEEEEE', // Light gray
      light: '#FFFFFF',
      dark: '#CCCCCC',
    },
    warning: {
      main: '#D1D1D1', // Light-mid gray
      light: '#EEEEEE',
      dark: '#ACACAC',
    },
    success: {
      main: '#ACACAC', // Mid gray
      light: '#D1D1D1',
      dark: '#858585',
    },
    info: {
      main: '#FFFFFF', // Pure white
      light: '#FFFFFF',
      dark: '#E0E0E0',
    },
    background: {
      default: '#1E1E1E', // Figma dark canvas
      paper: '#2C2C2C',   // Figma dark panels
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#ECECEC',
      disabled: '#8D8D8D',
    },
    divider: '#3D3D3D',
    grey: {
      50: '#F9F9F9',
      100: '#F2F2F2',
      200: '#E6E6E6',
      300: '#CCCCCC',
      400: '#B3B3B3',
      500: '#999999',
      600: '#6E6E6E',
      700: '#4D4D4D',
      800: '#333333',
      900: '#1A1A1A',
    },
    action: {
      active: '#FFFFFF',
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.25,
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.05em',
      lineHeight: 1.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.3)',
    '0px 2px 4px rgba(0, 0, 0, 0.3)',
    '0px 4px 8px rgba(0, 0, 0, 0.3)',
    '0px 6px 10px rgba(0, 0, 0, 0.3)',
    '0px 8px 12px rgba(0, 0, 0, 0.3)',
    '0px 10px 14px rgba(0, 0, 0, 0.3)',
    '0px 12px 16px rgba(0, 0, 0, 0.3)',
    '0px 14px 18px rgba(0, 0, 0, 0.3)',
    '0px 16px 20px rgba(0, 0, 0, 0.3)',
    '0px 18px 22px rgba(0, 0, 0, 0.3)',
    '0px 20px 24px rgba(0, 0, 0, 0.3)',
    '0px 22px 26px rgba(0, 0, 0, 0.3)',
    '0px 24px 28px rgba(0, 0, 0, 0.3)',
    '0px 26px 30px rgba(0, 0, 0, 0.3)',
    '0px 28px 32px rgba(0, 0, 0, 0.3)',
    '0px 30px 34px rgba(0, 0, 0, 0.3)',
    '0px 32px 36px rgba(0, 0, 0, 0.3)',
    '0px 34px 38px rgba(0, 0, 0, 0.3)',
    '0px 36px 40px rgba(0, 0, 0, 0.3)',
    '0px 38px 42px rgba(0, 0, 0, 0.3)',
    '0px 40px 44px rgba(0, 0, 0, 0.3)',
    '0px 42px 46px rgba(0, 0, 0, 0.3)',
    '0px 44px 48px rgba(0, 0, 0, 0.3)',
  ],
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C',
          borderBottom: '1px solid #3D3D3D',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3)',
          },
        },
        containedPrimary: {
          backgroundColor: '#FFFFFF',
          color: '#1E1E1E',
          '&:hover': {
            backgroundColor: '#E0E0E0',
          },
        },
        outlinedPrimary: {
          borderColor: '#FFFFFF',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
        textPrimary: {
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '& fieldset': {
              borderColor: '#3D3D3D',
            },
            '&:hover fieldset': {
              borderColor: '#6E6E6E',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFFFFF',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.4)',
          backgroundColor: '#2C2C2C',
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.4)',
        },
        elevation2: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.4)',
        },
        elevation3: {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)',
        },
        elevation4: {
          boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.4)',
          border: '1px solid #3D3D3D',
          backgroundColor: '#2C2C2C',
          backgroundImage: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minWidth: 100,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          minHeight: 36,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#3D3D3D',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #3D3D3D',
        },
        head: {
          fontWeight: 600,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#1E1E1E',
          backgroundImage: 'none',
          scrollbarColor: "#6E6E6E #2C2C2C",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            backgroundColor: "#2C2C2C",
            width: 8,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#6E6E6E",
            minHeight: 24,
          },
          "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
            backgroundColor: "#8D8D8D",
          },
          "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
            backgroundColor: "#8D8D8D",
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#8D8D8D",
          },
        },
      },
    },
  },
});

export default figmaDarkTheme;